import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Brain, Target, TrendingUp, AlertCircle, CheckCircle, Settings, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { OptimizationService } from '@/services/OptimizationService';
import { type TradingFile } from '@/services/TradingPatternAnalysisService';
import { AnalysisMode } from '@/types/trading-analysis-types';

interface AlternativeConfig {
  threshold: number;
  sequenceLength: number;
  accuracy: number;
  tradeoff: string;
}

interface OptimizationResult {
  bestThreshold: number;
  bestSequenceLength: number;
  expectedAccuracy: number;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  statisticalSignificance: number;
  riskAdjustedScore: number;
  alternativeConfigs: AlternativeConfig[];
  processingTime: number;
}

interface OptimizationRecommendationsProps {
  selectedFile: TradingFile | null;
  selectedAnalysisMode: AnalysisMode;
}

export const OptimizationRecommendations: React.FC<OptimizationRecommendationsProps> = ({
  selectedFile,
  selectedAnalysisMode
}) => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recommendations, setRecommendations] = useState<OptimizationResult | null>(null);

  const handleAutoOptimize = async () => {
    if (!selectedFile?.id) {
      toast.error('Please select a file first');
      return;
    }

    setIsOptimizing(true);
    setProgress(0);
    setRecommendations(null);

    try {
      const result = await OptimizationService.runAutomatedOptimization({
        fileId: selectedFile.id,
        analysisMode: selectedAnalysisMode,
        maxSequenceLength: 12,
        thresholdRange: [0.50, 0.90],
        targetMetric: 'accuracy' // Could be 'risk_adjusted', 'precision', etc.
      }, (progressUpdate) => {
        setProgress((progressUpdate.completed / progressUpdate.total) * 100);
      });

      if (result.success && result.data) {
        setRecommendations(result.data);
        toast.success('AI optimization completed successfully!');
      } else {
        toast.error(result.error || 'Failed to run AI optimization');
      }
    } catch (error) {
      console.error('AI optimization error:', error);
      toast.error('Failed to run AI optimization');
    } finally {
      setIsOptimizing(false);
      setProgress(0);
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'text-green-600 bg-green-50 border-green-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence) {
      case 'high': return <CheckCircle className="h-4 w-4" />;
      case 'medium': return <AlertCircle className="h-4 w-4" />;
      case 'low': return <AlertCircle className="h-4 w-4" />;
      default: return <Settings className="h-4 w-4" />;
    }
  };

  if (!selectedFile) {
    return (
      <div className="text-center py-8">
        <Brain className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-lg font-medium mb-2">No File Selected</p>
        <p className="text-sm text-muted-foreground">
          Please select a trading file to get AI-powered optimization recommendations
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">AI-Powered Optimization</h3>
          <p className="text-sm text-muted-foreground">
            Let AI find the optimal configuration for maximum performance
          </p>
        </div>
        <Badge variant="outline">File: {selectedFile.filename}</Badge>
      </div>

      {/* Auto Optimize Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Automated Parameter Optimization
          </CardTitle>
          <CardDescription>
            AI will analyze your data and recommend optimal threshold and sequence length parameters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium">Analysis Scope</div>
              <div className="text-muted-foreground">
                Thresholds: 50% - 90% • Sequences: 3 - 12 trades
              </div>
            </div>
            <div>
              <div className="font-medium">Optimization Target</div>
              <div className="text-muted-foreground">
                Risk-adjusted accuracy with statistical significance
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Expected processing time: 30-60 seconds
            </div>
            <Button 
              onClick={handleAutoOptimize} 
              disabled={isOptimizing}
              className="bg-primary hover:bg-primary/90"
            >
              <Zap className="h-4 w-4 mr-2" />
              {isOptimizing ? 'Optimizing...' : 'Auto Optimize'}
            </Button>
          </div>

          {isOptimizing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Running AI optimization...</span>
                <span>{progress.toFixed(0)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Recommendations */}
      {recommendations && (
        <div className="space-y-4">
          {/* Main Recommendation */}
          <Card className="border-muted bg-muted/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Recommended Configuration
                <Badge className={`ml-auto ${getConfidenceColor(recommendations.confidence)}`}>
                  {getConfidenceIcon(recommendations.confidence)}
                  {recommendations.confidence} confidence
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {(recommendations.bestThreshold * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Win Threshold</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {recommendations.bestSequenceLength}
                  </div>
                  <div className="text-sm text-muted-foreground">Sequence Length</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {recommendations.expectedAccuracy.toFixed(1)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Expected Accuracy</div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="font-medium">AI Reasoning:</div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {recommendations.reasoning}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-medium">Statistical Significance</div>
                  <div className="text-muted-foreground">
                    {(recommendations.statisticalSignificance * 100).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="font-medium">Risk-Adjusted Score</div>
                  <div className="text-muted-foreground">
                    {recommendations.riskAdjustedScore.toFixed(2)}/10.0
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1">
                  Apply Configuration
                </Button>
                <Button variant="outline">
                  Customize
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Alternative Configurations */}
          {recommendations.alternativeConfigs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Alternative Configurations
                </CardTitle>
                <CardDescription>
                  Other high-performing configurations you might consider
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recommendations.alternativeConfigs.slice(0, 3).map((config, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="font-medium">{(config.threshold * 100).toFixed(0)}%</div>
                          <div className="text-xs text-muted-foreground">Threshold</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium">{config.sequenceLength}</div>
                          <div className="text-xs text-muted-foreground">Sequence</div>
                        </div>
                        <div className="text-center">
                          <div className="font-medium">{config.accuracy.toFixed(1)}%</div>
                          <div className="text-xs text-muted-foreground">Accuracy</div>
                        </div>
                      </div>
                      <div className="text-right flex-1 max-w-xs">
                        <div className="text-sm text-muted-foreground">
                          {config.tradeoff}
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        Use This
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Performance Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Optimization Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-medium">Processing Time</div>
                  <div className="text-muted-foreground">
                    {(recommendations.processingTime / 1000).toFixed(1)} seconds
                  </div>
                </div>
                <div>
                  <div className="font-medium">Configurations Tested</div>
                  <div className="text-muted-foreground">
                    {(8 * 10)} combinations
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};